import { colors, cssThemedValue } from '@creditornot/cb-ingredients';
import { Tag } from '@creditornot/cb-tag';
import React, { useContext } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Option, OptionType } from 'apis/restaurant-api/merchant-admin/menu2/types';
import { WideAreaCheckbox } from 'common/components';
import { Typography as T } from 'common/typography';
import { MenuLocaleContext } from 'modules/menu/MenuLocaleContext';
import OptionChoicesWithPopover from 'modules/menu/options/components/OptionChoicesWithPopover';

type Props = {
  className?: string;
  onDeselect: () => void;
  onSelect: () => void;
  option: Omit<Option, 'name'> & {
    name: string;
  };
  selected: boolean;
};

const optionTypes: { [key in OptionType]: string } = {
  bool: 'menu-option.switch',
  choice: 'menu-option.single-choice',
  multi_choice: 'menu-option.multi-choice',
};

const Root = styled(T.Body3)<{ selected: boolean }>`
  width: 100%;
  display: flex;
  align-items: center;
  height: 52px;
  padding: 16px 24px 16px 0;
  flex-shrink: 0;
  background-color: ${({ selected }) => (selected ? colors.wolt4 : cssThemedValue('surfaceMain'))};
  transition: background-color 100ms;
  cursor: pointer;
  overflow: hidden;
  white-space: nowrap;

  &:hover {
    background-color: ${({ selected }) =>
      selected ? colors.wolt8 : cssThemedValue({ light: colors.pepper8, dark: colors.salt4 })};
  }
`;

const Name = styled(T.Title2)`
  margin-inline-end: 16px;
`;

const OptionTypeTag = styled(Tag).attrs({ size: 'tiny', uppercase: true, variant: 'lightBlack' })`
  margin-inline-end: 16px;
`;

const OptionRowItem = ({ className, onDeselect, onSelect, option, selected }: Props) => {
  const { primaryLanguage } = useContext(MenuLocaleContext);

  const handleSelectToggle = () => {
    if (!!selected) {
      onDeselect();
    } else {
      onSelect();
    }
  };

  return (
    <Root className={className} selected={selected} onClick={handleSelectToggle}>
      <WideAreaCheckbox onToggle={handleSelectToggle} selectionStatus={selected ? 'selected' : 'not-selected'} />
      <Name>{option.name}</Name>
      <OptionTypeTag>
        <FormattedMessage id={optionTypes[option.type]} />
      </OptionTypeTag>
      <OptionChoicesWithPopover
        onClick={handleSelectToggle}
        optionChoices={option.values}
        primaryLanguage={primaryLanguage}
      />
    </Root>
  );
};

export default OptionRowItem;
